import React from 'react';

import { LoadingSwitch } from '../../../core';
import useDataFetcher from '../../../useDataFetcher';
import { useLegalAction } from '../../hooks/useLegalAction';
import { useLegalActionsBackendRoutes } from '../../hooks/useLegalActionsBackendRoutes';
import type {
  DocumentApiResponse,
  LegalActionLogApiResponse,
  NegotiationLogApiResponse,
  ProsAndConsApiResponse,
} from '../../types';

import { LegalActionDocumentsCard } from './Documents/LegalActionDocumentsCard';
import { LegalActionLogsCard } from './Logs/LegalActionLogsCard';
import { NegotiationLogsCard } from './Negotiations/NegotiationLogsCard';
import { NotesCard } from './Notes/NotesCard';
import { ProsAndConsCard } from './ProsAndCons/ProsAndConsCard';

export const LogsTab: React.FC = () => {
  const { legalAction } = useLegalAction();
  const legalActionId = legalAction?.id;
  const backendRoutes = useLegalActionsBackendRoutes(legalActionId);

  const {
    data: logs,
    isLoading: isLogsLoading,
    isError: isLogsError,
    reloadData: reloadLogs,
  } = useDataFetcher<(LegalActionLogApiResponse | ProsAndConsApiResponse | NegotiationLogApiResponse)[]>(
    backendRoutes.legalAction.logs.base
  );

  const {
    data: documents,
    isLoading: isDocumentsLoading,
    isError: isDocumentsError,
    reloadData: reloadDocuments,
  } = useDataFetcher<DocumentApiResponse[]>(backendRoutes.legalAction.documents);

  if (!legalAction) return null;

  const reloadLogsVoid = async () => {
    await reloadLogs();
  };

  return (
    <>
      <LoadingSwitch isLoading={isLogsLoading} isError={isLogsError}>
        <div className="flex w-1/2 flex-col gap-24">
          <LegalActionLogsCard logs={logs as LegalActionLogApiResponse[]} reloadLogs={reloadLogsVoid} />
          <ProsAndConsCard logs={logs as ProsAndConsApiResponse[]} reloadLogs={reloadLogsVoid} />
          <NegotiationLogsCard logs={logs as NegotiationLogApiResponse[]} reloadLogs={reloadLogsVoid} />
          <LoadingSwitch isLoading={isDocumentsLoading} isError={isDocumentsError}>
            <LegalActionDocumentsCard
              documents={documents as DocumentApiResponse[]}
              reloadDocuments={async () => {
                await reloadDocuments();
              }}
            />
          </LoadingSwitch>
        </div>
        <div className="flex w-1/2 flex-col gap-24">
          <NotesCard />
        </div>
      </LoadingSwitch>
    </>
  );
};
